// errorHandler.ts
export const isChunkLoadError = (error: Error): boolean => {
  if (!error || !error.message) return false;

  const patterns = [
    /Loading chunk/i,
    /Loading CSS chunk/i,
    /Failed to fetch dynamically imported module/i,
    /Expected a JavaScript-or-Wasm module script but the server responded with a MIME type of "text\/html"/i,
    /ChunkLoadError/i,
  ];

  return patterns.some((pattern) => pattern.test(error.message));
};

export const clearCacheAndReload = async (): Promise<void> => {
  try {
    // Clear service worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName))
      );
    }

    // Clear localStorage version info
    localStorage.removeItem('app-version');

    // Force reload without cache
    window.location.reload();
  } catch (error) {
    console.error('Failed to clear cache:', error);
    // Fallback to simple reload
    window.location.reload();
  }
};

export const setupGlobalErrorHandlers = (): void => {
  // Handle unhandled promise rejections (like dynamic import failures)
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);

    if (event.reason && isChunkLoadError(event.reason)) {
      event.preventDefault(); // Prevent the default error reporting
      console.log('Detected chunk load error, clearing cache and reloading...');
      clearCacheAndReload();
    }
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);

    if (event.error && isChunkLoadError(event.error)) {
      console.log('Detected chunk load error, clearing cache and reloading...');
      clearCacheAndReload();
    }
  });
};

// App version checking
export const checkAppVersion = async (): Promise<boolean> => {
  try {
    // Get build timestamp from a meta tag or API endpoint
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-cache',
    });

    if (response.ok) {
      const { version } = await response.json();
      const currentVersion = localStorage.getItem('app-version');

      if (currentVersion && currentVersion !== version) {
        localStorage.setItem('app-version', version);
        return true; // Version changed
      }

      if (!currentVersion) {
        localStorage.setItem('app-version', version);
      }
    }
  } catch (error) {
    console.warn('Failed to check app version:', error);
  }

  return false;
};
